import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://mikassa.app";
const SITE_NAME = "Mikassa Agency";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;
const DEFAULT_DESCRIPTION = "Logiciel de gestion locative pour l'Afrique. Gérez vos biens, locataires et paiements en toute simplicité. Réduisez les impayés de 90%.";

// Organization Schema
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Mikassa",
  alternateName: "Mikassa Agency",
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  description: "Logiciel de gestion locative pour l'Afrique de l'Ouest",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Abidjan",
    addressCountry: "CI"
  },
  email: "contact@mikassa.app",
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+2250595476593",
    contactType: "customer service",
    availableLanguage: ["French", "English"]
  },
  sameAs: [
    "https://www.linkedin.com/company/mikassa-agency",
    "https://web.facebook.com/profile.php?id=61577178491667"
  ]
};

// Software Application Schema
const softwareSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Mikassa Agency",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web, iOS, Android",
  description: "Logiciel de gestion locative pour propriétaires et agences immobilières en Afrique",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "XOF",
    description: "Essai gratuit disponible"
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    ratingCount: "150"
  }
};

const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url,
  type = "website",
  article = null,
  noindex = false,
  lang = "fr",
  alternateLanguages = [],
  breadcrumbs = [],
  faqs = [],
  reviews = [],
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Gestion Locative Simplifiée`;
  const canonicalUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  const fullImage = image.startsWith("http") ? image : `${SITE_URL}${image}`;

  // Article Schema for blog posts
  const articleSchema = article ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.title,
    description: article.description,
    image: fullImage,
    author: {
      "@type": "Organization",
      name: article.author || "Mikassa"
    },
    publisher: {
      "@type": "Organization",
      name: "Mikassa",
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/logo.png`
      }
    },
    datePublished: article.publishedAt,
    dateModified: article.updatedAt || article.publishedAt,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonicalUrl
    },
    keywords: article.keywords?.join(", ") || ""
  } : null;

  // Breadcrumb Schema
  const breadcrumbSchema = breadcrumbs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: `${SITE_URL}${crumb.url}`
    }))
  } : null;

  // FAQ Schema
  const faqSchema = faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(faq => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer
      }
    }))
  } : null;

  // Review Schema
  const reviewSchema = reviews.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Mikassa Agency",
    description: "Logiciel de gestion locative pour l'Afrique",
    brand: {
      "@type": "Brand",
      name: "Mikassa"
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      reviewCount: String(reviews.length),
      bestRating: "5",
      worstRating: "1"
    },
    review: reviews.map(review => ({
      "@type": "Review",
      author: {
        "@type": "Person",
        name: review.author
      },
      reviewRating: {
        "@type": "Rating",
        ratingValue: review.rating || "5",
        bestRating: "5",
        worstRating: "1"
      },
      reviewBody: review.body,
      datePublished: review.date || "2024-01-01"
    }))
  } : null;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={lang === "fr" ? "fr_FR" : "en_US"} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* Alternate Languages (hreflang) */}
      {alternateLanguages.map((alt) => (
        <link
          key={alt.lang}
          rel="alternate"
          hrefLang={alt.lang}
          href={`${SITE_URL}${alt.url}`}
        />
      ))}
      {alternateLanguages.length > 0 && (
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
      )}

      {/* Article specific meta */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedAt} />
          {article.updatedAt && (
            <meta property="article:modified_time" content={article.updatedAt} />
          )}
          <meta property="article:author" content={article.author || "Mikassa"} />
          {article.tags?.map((tag) => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>

      {type === "website" && (
        <script type="application/ld+json">
          {JSON.stringify(softwareSchema)}
        </script>
      )}

      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}

      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}

      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}

      {reviewSchema && (
        <script type="application/ld+json">
          {JSON.stringify(reviewSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
